import type { ApiDomainConfig } from '#types/featureFlags'
import { parseURL } from 'ufo'

/**
 * Matches a request path against a pattern.
 *
 * Supports two pattern types:
 * 1. **Simple prefix** (no leading `^`): Fast string prefix matching
 * 2. **Regex pattern** (starts with `^`): Full regex matching
 *
 * @param requestPath - The actual request path (e.g., "/products/ABC123/reviews")
 * @param pattern - The pattern to match against (prefix string or regex)
 * @returns True if the path matches the pattern
 *
 * @example
 * ```ts
 * // Simple prefix
 * matchesPattern("/products/v2/123", "/products/v2")
 * // Returns: true
 *
 * // Regex pattern
 * matchesPattern("/products/ABC123/reviews", "^/products/[^/]+/reviews")
 * // Returns: true
 * ```
 */
function matchesPattern(requestPath: string, pattern: string): boolean {
  // Fast path: simple prefix match for non-regex patterns
  if (!pattern.startsWith('^')) {
    return requestPath.startsWith(pattern)
  }

  // Regex pattern: compile and test
  const regex = new RegExp(pattern)
  return regex.test(requestPath)
}

/**
 * Resolves the appropriate base URL for an API request based on path-specific routing rules.
 *
 * When `pathMappings` are configured in the LaunchDarkly `configApiDomains` flag,
 * this function checks if the request path matches any routing pattern and replaces
 * the path component of the base URL accordingly.
 *
 * Supports two pattern types:
 * 1. **Simple prefix** (no `^`): `/products/v2` matches `/products/v2/123`
 * 2. **Regex pattern** (starts with `^`): `^/products/[^/]+/reviews` matches `/products/ABC123/reviews`
 *
 * @param requestPath - The API endpoint path to match against routing patterns (e.g., "/products/v2/123")
 * @param config - The API domain configuration from LaunchDarkly containing base URLs and path mappings
 * @param isSSR - Whether this is a server-side request (enables SSR-specific URL overrides)
 * @returns The resolved base URL for this endpoint
 *
 * @example
 * ```ts
 * // Simple prefix matching
 * const config = {
 *   apiBaseUrl: "https://www.timberland.com/api",
 *   pathMappings: [
 *     { pattern: "/products/v2", path: "/napi" }
 *   ]
 * }
 * resolveApiBaseUrl("/products/v2/123", config, false)
 * // Returns: "https://www.timberland.com/napi"
 *
 * // Regex pattern matching
 * const configWithRegex = {
 *   apiBaseUrl: "https://www.timberland.com/api",
 *   pathMappings: [
 *     { pattern: "^/products/v1/products/[^/]+/reviews", path: "/api" }
 *   ]
 * }
 * resolveApiBaseUrl("/products/v1/products/ABC123/reviews", configWithRegex, false)
 * // Returns: "https://www.timberland.com/api"
 * ```
 *
 * @category API
 */
export function resolveApiBaseUrl(
  requestPath: string,
  config: ApiDomainConfig,
  isSSR: boolean = false
): string {
  const { apiBaseUrl, apiCutoverUrlSSR, pathMappings = [] } = config

  // Get base URL (SSR override if applicable)
  const baseUrl = (isSSR && apiCutoverUrlSSR) || apiBaseUrl

  // No mappings? Return base URL as-is
  if (!pathMappings.length) return baseUrl

  // Find first matching path mapping
  const matchedMapping = pathMappings.find((mapping) =>
    matchesPattern(requestPath, mapping.pattern)
  )

  if (matchedMapping) {
    // Replace the path component of baseUrl with the mapped path
    const { protocol, host } = parseURL(baseUrl)
    return `${protocol}//${host}${matchedMapping.path}`
  }

  // No match, return base URL
  return baseUrl
}

/**
 * Resolves the appropriate base URL for a CMS request.
 *
 * Currently uses the same logic as API resolution but without path mappings.
 * Structure is ready if CMS-specific routing rules are needed in the future.
 *
 * @param requestPath - The CMS endpoint path (currently unused, reserved for future routing)
 * @param config - The API domain configuration from LaunchDarkly
 * @param isSSR - Whether this is a server-side request
 * @returns The resolved CMS base URL
 *
 * @category API
 */
export function resolveCmsBaseUrl(
  requestPath: string,
  config: ApiDomainConfig,
  isSSR: boolean = false
): string {
  const { cmsBaseUrl, cmsCutoverUrlSSR } = config
  return (isSSR && cmsCutoverUrlSSR) || cmsBaseUrl
}
